import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reportService } from '../../services/api/reports';
import type {
  DraftNarrativeRequest,
  ReportNarrative,
} from '../../services/api/reports';
import {
  ReportRequest,
  ReportRequestCreate,
  ReportFilters,
} from '../../types/api';

export const useReports = (filters?: ReportFilters) => {
  return useQuery<ReportRequest[]>({
    queryKey: ['reports', filters],
    queryFn: () => reportService.getReports(filters),
    // Poll the list while any report is in flight so status badges and
    // download/view actions appear without a manual refresh after the
    // background task lands. Falls back to no polling once everything
    // is settled (completed/failed) -- typical case for an idle page.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const hasInFlight = data.some(
        (r) => r.status === 'pending' || r.status === 'generating'
      );
      return hasInFlight ? 3000 : false;
    },
  });
};

export const useReport = (id: string, enabled = true) => {
  return useQuery<ReportRequest>({
    queryKey: ['reports', id],
    queryFn: () => reportService.getReport(id),
    enabled: enabled && !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      // Poll every 2s while report is being generated
      if (status === 'pending' || status === 'generating') return 2000;
      return false;
    },
  });
};

export const useCreateReport = () => {
  const queryClient = useQueryClient();

  return useMutation<ReportRequest, Error, ReportRequestCreate>({
    mutationFn: (data) => reportService.createReport(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
};

export const useDeleteReport = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (id) => reportService.deleteReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
};

export const useRegenerateReport = () => {
  const queryClient = useQueryClient();

  return useMutation<ReportRequest, Error, string>({
    mutationFn: (id) => reportService.regenerateReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
};

export const useDownloadReport = () => {
  return useMutation<Blob, Error, { id: string; filename: string }>({
    mutationFn: ({ id }) => reportService.downloadReport(id),
    onSuccess: (blob, { filename }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    },
  });
};

/**
 * Generate an AI-drafted term-report narrative for a single child.
 *
 * Mutation rather than query because the call is expensive (10-60s on
 * llama3.1:8b) and should only fire on explicit user action. The hook
 * does not invalidate any queries -- the result is held in mutation
 * state and the parent component owns the editing flow.
 */
export const useDraftReportNarrative = () => {
  return useMutation<ReportNarrative, Error, DraftNarrativeRequest>({
    mutationFn: (req) => reportService.draftNarrative(req),
  });
};
